import React from 'react'
import { css } from '@emotion/css'
import { FormattedRelativeTime, FormattedTime, FormattedDate } from 'react-intl'
import { readOnlyGridCss } from '../../../legacy-design-system/text'
import { CellRenderer, CellProps } from '../../../legacy-design-system/grid'
import { LoadingTextMask } from '../../../legacy-design-system/loading-masks'
import { WithTooltip } from '../../../design-system'
import { selectRelativeUnits } from '../../react-intl-helpers'
import { DateTime, VulnerabilityDateField } from '../shared'

const cellCss = css`
  padding: 8px;
`

interface GetRendererProps {
  dateField?: VulnerabilityDateField
}

interface Vulnerability {
  firstDetected: DateTime
  lastDetected: DateTime
}

interface SinkGroup {
  firstDetected: DateTime
  lastDetected: DateTime
}

interface RowData {
  sinkGroup?: SinkGroup
  vulnerability?: Vulnerability
}

export function getDateDetectedCellRenderer({
  dateField
}: GetRendererProps): CellRenderer<RowData> {
  return ({ rowData, getCellContentProps }) => {
    const data = rowData.vulnerability
      ? rowData.vulnerability
      : rowData.sinkGroup

    if (!data) {
      throw new Error(
        `Invalid date-detected row. Row data should contain a vulnerability or sink group: ${rowData}`
      )
    }
    return renderDateCell({
      dateField,
      rowData: data,
      getCellContentProps
    })
  }
}

function renderDateCell({
  dateField,
  rowData: { firstDetected, lastDetected },
  getCellContentProps
}: GetRendererProps & CellProps<Vulnerability | SinkGroup>) {
  const dateDetected = new Date(
    dateField === 'LAST' ? lastDetected : firstDetected
  )
  const { value, unit } = selectRelativeUnits(dateDetected)

  return (
    <WithTooltip
      tiny={true}
      renderTooltipContent={() => (
        <FormattedDate value={dateDetected}>
          {(dateText) => (
            <FormattedTime value={dateDetected}>
              {(timeText) => <span children={`${dateText} ${timeText}`} />}
            </FormattedTime>
          )}
        </FormattedDate>
      )}
      children={() => (
        <div {...getCellContentProps({ className: cellCss })}>
          <FormattedRelativeTime value={value} unit={unit} numeric="auto">
            {(text) => <span className={readOnlyGridCss} children={text} />}
          </FormattedRelativeTime>
        </div>
      )}
    />
  )
}

export function loadingLastDetectedCellRenderer() {
  return <LoadingTextMask min={6} max={7} />
}
